<template>
    <v-layout justify-center>
        <v-responsive width="100%" class="px-3 mx-n3">
            <list-heading title="재고주문 관리" showsFilterButton v-model="showsSearch" />
            <list-search-default v-bind="{ showsSearch, loading, searchKeys }" />

            <v-row class="ml-0 mt-0">
                <v-spacer />
                <v-col class="pl-0 pt-0" cols="auto">
                    <v-sheet outlined rounded>
                        <v-btn color="white" class="green--text px-2" v-bind="{ loading }" @click="excel"><v-icon class="mr-2">mdi-microsoft-excel</v-icon>엑셀다운로드</v-btn>
                    </v-sheet>
                </v-col>
            </v-row>

            <v-data-table v-bind="{ items, headers, loading }" :items-per-page="-1" disable-pagination disable-sort hide-default-footer class="v-sheet--outlined">
                <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }"> {{ header.formatter(value, item) }} </template>
            </v-data-table>

            <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-2" @input="(page) => $router.push({ query: { ...$route.query, page } })" />
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import dayjs from "@/plugins/vue-dayjs";
import { PURCHASE_CLAIM_STATUSES, PURCHASE_TYPES } from "@/assets/variables";

import ListHeading from "@/components/console/dumb/list-heading.vue";
import ListSearchDefault from "@/components/console/dumb/list-search-default.vue";

const headers = [
    { value: "type", text: "유형", formatter: (value) => PURCHASE_TYPES[value]?.text || "-" },

    { value: "user", text: "사용자 (ID/회사명)", formatter: (value) => [value?.username, value?.name].join(" / ") },
    { value: "fullName", text: "상품명" },

    { value: "stockAt", text: "입고일", formatter: (value, item) => (item?.type == PURCHASE_TYPES.STOCK.value ? item?.createdAt?.toDateTime?.() || item?.createdAt || "-" : "-") },

    { value: "stoutAt", text: "출고일", formatter: (value, item) => (item?.type == PURCHASE_TYPES.STOUT.value ? item?.createdAt?.toDateTime?.() || item?.createdAt || "-" : "-") },

    { value: "createdAt", text: "잔여보관일", formatter: (value) => dayjs(value).add(90, "days").startOf("day").diff(dayjs().startOf("day"), "days") },

    { value: "amount", text: "재고수량", formatter: (value = 0, item) => ({ [PURCHASE_TYPES.STOCK.value]: value, [PURCHASE_TYPES.STOUT.value]: value * -1 }[item?.type] || "-") },

    { value: "purchasePrice__unit", text: "단가(원가)", formatter: (value) => value?.format?.() || value || "-" },

    { value: "purchasePrice__total", text: "총액(수량X단가)", formatter: (value) => value?.format?.() || value || "-" },
].map((item, index, array) => ({ ...item, formatter: item.formatter ?? ((value) => value ?? "-"), cellClass: "caption", divider: index != array.length - 1 }));

const searchKeys = [
    {
        value: "fullName",
        text: "상품명",
    },
    {
        value: "_user",
        text: "사용자",
    },
];

export default {
    components: {
        ListHeading,
        ListSearchDefault,
    },
    setup: () => ({
        headers,
        searchKeys,
    }),
    data: () => ({
        purchases: [],

        limit: 10,
        summary: { totalCount: 0 },

        loading: false,
        showsSearch: true,
    }),
    computed: {
        items() {
            return [...this.purchases];
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            query.type = JSON.stringify({ $in: [PURCHASE_TYPES.STOCK.value, PURCHASE_TYPES.STOUT.value] });
            query.claimStatus = JSON.stringify({ $nin: [PURCHASE_CLAIM_STATUSES.CANCEL_COMPLETED.value] });

            return { ...query };
        },
    },
    mounted() {
        this.init();
    },
    watch: {
        params() {
            this.search();
        },
    },
    methods: {
        async init() {
            try {
                await this.search();
            } catch (error) {
                console.error(error);
            }
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { skip, limit, params } = this;
                var { summary, purchases } = await api.console.shop.purchases.getPurchases({
                    headers: { skip, limit },
                    params,
                });

                this.summary = summary;
                this.purchases = purchases;
            } finally {
                this.loading = false;
            }
        },

        async excel() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { params } = this;
                let { purchases } = await api.console.shop.purchases.getPurchases({ params });

                let fileName = `재고주문_목록`;
                this.$excel(purchases, this.headers, fileName);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .white-space-pre-line {
        white-space: pre-line;
    }
    .v-pagination button {
        box-shadow: none !important;
        border: thin solid rgba(0, 0, 0, 0.12);
    }
}
</style>
